import { Check } from "lucide-react";

const Dots = () => (
  <div className="flex gap-1.5">
    {["#FF6B5C", "#FFC24B", "#3FCF8E"].map((c, i) => (
      <span key={i} className="h-2.5 w-2.5 rounded-full" style={{ background: c, opacity: 0.85 }} />
    ))}
  </div>
);

const Tile = ({ label, value }) => (
  <div className="rounded-[8px] p-2.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(231,238,248,0.10)" }}>
    <p className="text-[7px] uppercase tracking-[0.12em]" style={{ color: "rgba(231,238,248,0.42)", fontFamily: "'IBM Plex Mono', monospace" }}>{label}</p>
    <p className="font-display mt-1 text-lg leading-none" style={{ color: "#E7EEF8" }}>{value}</p>
  </div>
);

const Bars = ({ accent, data = [40, 62, 48, 78, 56, 90, 66, 82] }) => (
  <div className="flex h-[72px] items-end gap-1.5">
    {data.map((h, i) => (
      <span key={i} className="flex-1 rounded-[2px]" style={{ height: `${h}%`, background: `linear-gradient(${accent}, ${accent}33)`, opacity: 0.6 }} />
    ))}
  </div>
);

const MockContent = ({ variant, accent }) => {
  switch (variant) {
    case "flow":
      return (
        <div className="space-y-2">
          {[["Signs up", "Trigger"], ["Welcome #1", "Email"], ["Wait 2 days", "Delay"], ["Offer + SMS", "Branch"]].map(([t, k], i) => (
            <div key={i} className="flex items-center gap-2.5">
              <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-[7px] text-[10px]" style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}44`, fontFamily: "'IBM Plex Mono', monospace" }}>{i + 1}</span>
              <div className="flex flex-1 items-center justify-between rounded-[7px] px-3 py-2" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(231,238,248,0.10)" }}>
                <span className="text-[11px]" style={{ color: "rgba(231,238,248,0.82)" }}>{t}</span>
                <span className="text-[8px] uppercase tracking-[0.1em]" style={{ color: "rgba(231,238,248,0.42)", fontFamily: "'IBM Plex Mono', monospace" }}>{k}</span>
              </div>
            </div>
          ))}
        </div>
      );
    case "gauge":
      return (
        <div className="flex items-center gap-4">
          <div className="relative h-[88px] w-[88px] flex-shrink-0">
            <svg viewBox="0 0 100 100" className="h-full w-full">
              <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" />
              <circle cx="50" cy="50" r="40" fill="none" stroke={accent} strokeWidth="9" strokeLinecap="round" strokeDasharray="251" strokeDashoffset="13" transform="rotate(-90 50 50)" />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
              <span className="font-display text-xl" style={{ color: "#E7EEF8" }}>98%</span>
            </div>
          </div>
          <div className="flex-1 space-y-1.5">
            {["SPF", "DKIM", "DMARC"].map((k) => (
              <div key={k} className="flex items-center justify-between rounded-[7px] px-2.5 py-1.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(231,238,248,0.10)" }}>
                <span className="text-[10px]" style={{ color: "rgba(231,238,248,0.62)", fontFamily: "'IBM Plex Mono', monospace" }}>{k}</span>
                <span className="flex items-center gap-1 text-[9px]" style={{ color: accent }}><Check className="h-3 w-3" /> Pass</span>
              </div>
            ))}
          </div>
        </div>
      );
    case "editor":
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-[8px] px-3 py-2 text-[11px]" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(231,238,248,0.10)", color: "rgba(231,238,248,0.82)" }}>Your cart is still warm</div>
            <span className="font-display grid h-9 w-9 place-items-center rounded-[8px] text-base" style={{ background: `${accent}1f`, color: accent, border: `1px solid ${accent}44` }}>92</span>
          </div>
          <div className="space-y-1.5">
            {[100, 94, 82, 90, 70].map((w, i) => (
              <div key={i} className="h-2 rounded-full" style={{ width: `${w}%`, background: i === 0 ? `${accent}55` : "rgba(255,255,255,0.06)" }} />
            ))}
          </div>
        </div>
      );
    case "segments": {
      const hole = "#0A1422";
      return (
        <div className="flex items-center gap-4">
          <div className="relative h-[92px] w-[92px] flex-shrink-0 rounded-full" style={{ background: `conic-gradient(${accent} 0 41%, #B7FF5A 41% 74%, #FFC24B 74% 100%)` }}>
            <div className="absolute inset-[14px] rounded-full" style={{ background: hole }} />
          </div>
          <div className="flex-1 space-y-2">
            {[["VIP", "41%", accent], ["Active", "33%", "#B7FF5A"], ["At-risk", "26%", "#FFC24B"]].map(([k, v, c]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-[11px]" style={{ color: "rgba(231,238,248,0.62)" }}><span className="h-2 w-2 rounded-full" style={{ background: c }} />{k}</span>
                <span className="text-[11px]" style={{ color: "rgba(231,238,248,0.82)", fontFamily: "'IBM Plex Mono', monospace" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "templates":
      return (
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-[8px]" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(231,238,248,0.10)" }}>
              <div className="h-7" style={{ background: `linear-gradient(135deg, ${accent}33, transparent)` }} />
              <div className="space-y-1 p-2">
                <div className="h-1.5 w-3/4 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }} />
                <div className="h-1.5 w-1/2 rounded-full" style={{ background: "rgba(255,255,255,0.07)" }} />
              </div>
            </div>
          ))}
        </div>
      );
    case "calendar": {
      const hot = new Set([3, 9, 11, 16, 19]);
      return (
        <div>
          <div className="mb-1.5 grid grid-cols-7 gap-1">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <span key={i} className="text-center text-[8px]" style={{ color: "rgba(231,238,248,0.42)", fontFamily: "'IBM Plex Mono', monospace" }}>{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 21 }).map((_, i) => (
              <div key={i} className="grid aspect-square place-items-center rounded-[5px] text-[8px]"
                style={hot.has(i)
                  ? { background: `${accent}2e`, color: accent, border: `1px solid ${accent}55` }
                  : { background: "rgba(255,255,255,0.03)", color: "rgba(231,238,248,0.42)" }}>
                {i + 1}
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "alerts":
      return (
        <div className="space-y-2">
          {[["#B7FF5A", "Welcome flow resumed", "2m"], ["#FFC24B", "Open rate dipped 8%", "1h"], ["#FF6B5C", "Abandon flow paused", "3h"], [accent, "New segment synced", "5h"]].map(([c, t, time], i) => (
            <div key={i} className="flex items-center gap-2.5 rounded-[7px] px-3 py-2" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(231,238,248,0.10)" }}>
              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: c, boxShadow: `0 0 8px ${c}` }} />
              <span className="flex-1 text-[11px]" style={{ color: "rgba(231,238,248,0.82)" }}>{t}</span>
              <span className="text-[9px]" style={{ color: "rgba(231,238,248,0.42)", fontFamily: "'IBM Plex Mono', monospace" }}>{time}</span>
            </div>
          ))}
        </div>
      );
    case "report":
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px]" style={{ color: "rgba(231,238,248,0.62)" }}>Weekly report</span>
            <span className="font-display text-base" style={{ color: accent }}>+18%</span>
          </div>
          <svg viewBox="0 0 220 64" className="h-16 w-full" preserveAspectRatio="none">
            <polyline points="0,52 30,44 60,48 90,30 120,34 150,18 180,24 220,8" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="0,52 30,44 60,48 90,30 120,34 150,18 180,24 220,8" fill={`${accent}14`} stroke="none" transform="translate(0,0)" style={{ display: "none" }} />
          </svg>
          <div className="grid grid-cols-2 gap-2">
            <Tile label="Sent" value="84.2K" />
            <Tile label="Revenue" value="$31K" />
          </div>
        </div>
      );
    case "analytics":
    default:
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Tile label="Revenue" value="$4.2K" />
            <Tile label="Opens" value="44%" />
            <Tile label="CTR" value="7.3%" />
          </div>
          <Bars accent={accent} />
        </div>
      );
  }
};

export const BrowserMock = ({ variant = "analytics", accent = "#6FE7FF", url = "app.emailink.studio", h = 168, className = "" }) => (
  <div className={`overflow-hidden rounded-[14px] ${className}`} style={{ background: "linear-gradient(155deg,#0C1A2C,#060D18)", border: "1px solid var(--stroke-1)", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}>
    <div className="flex items-center gap-3 border-b px-4 py-3" style={{ borderColor: "rgba(231,238,248,0.10)" }}>
      <Dots />
      <div className="ml-1 flex-1 truncate rounded-md px-3 py-1.5 text-[10px]" style={{ background: "rgba(255,255,255,0.04)", color: "rgba(231,238,248,0.42)", fontFamily: "'IBM Plex Mono', monospace" }}>{url}</div>
    </div>
    <div className="overflow-hidden p-4 sm:p-5" style={{ height: h }}>
      <MockContent variant={variant} accent={accent} />
    </div>
  </div>
);
